import { type Dispatch, type MouseEvent, type SetStateAction } from 'react';
import classNames from 'classnames';

import { type ClickType } from '@jsmdg/tracking';
import { ProductType } from '../../../shared/enums';
import { isExperience } from '../../../shared/product-safeguards';
import { isDeliveryFormatGroup } from '../../helper/deliveryFormat';
import { type UseBuyboxReturn } from '../../hooks/useBuybox';
import { useProduct } from '../../hooks/useProduct';
import { BuyboxBody } from '../BuyboxBody/BuyboxBody';
import { BuyboxHeader } from '../BuyboxHeader/BuyboxHeader';
import { ProductGroup } from '../ProductGroup';
import { ProductStateContainer } from '../ProductStateContainer';

export type BuyBoxProps = {
    readonly setTargetElement: Dispatch<SetStateAction<HTMLElement | null>>;
    readonly isCheckoutDisabled: boolean;
    readonly callToActionText: string;
    readonly showPayPalButton?: boolean;
    readonly buyboxProps: UseBuyboxReturn;
    readonly handleBuyboxButtonClick: (
        event?: MouseEvent<HTMLButtonElement>,
        clickType?: ClickType,
    ) => Promise<void> | void;
};

const Buybox = ({
    buyboxProps,
    callToActionText,
    handleBuyboxButtonClick,
    isCheckoutDisabled,
    setTargetElement,
    showPayPalButton = false,
}: BuyBoxProps): JSX.Element => {
    const product = useProduct();

    const { productGroup, productId, type } = product;

    return (
        <div
            data-testid="stickyBuyboxWrapper"
            className={classNames('pt-0 pt-sm-6x', {
                'h-100': isExperience(product),
                'sticky-sm-top': type !== ProductType.EXPERIENCE,
            })}
        >
            <div className="d-flex flex-wrap align-items-center">
                <BuyboxHeader />
            </div>
            <ProductStateContainer />

            {!isDeliveryFormatGroup(productGroup) && (
                <ProductGroup
                    productType={type}
                    selectedProductId={productId}
                    items={productGroup}
                />
            )}

            <BuyboxBody
                buyboxProps={buyboxProps}
                setTargetElement={setTargetElement}
                callToActionText={callToActionText}
                isCheckoutDisabled={isCheckoutDisabled}
                handleBuyboxButtonClick={handleBuyboxButtonClick}
                showPayPalButton={showPayPalButton}
            />
        </div>
    );
};

export { Buybox };
